/**
 * BollaFormModal — wrapper unificato per aprire il modal Bolla.
 *
 * Serve come SINGLE SOURCE OF TRUTH per qualunque punto dell'app che vuole
 * aprire una bolla (pagina Bolle principale, icona € nei listati Cantieri /
 * Manutenzioni, tab Bolle dentro il dettaglio Cantiere, ecc.).
 *
 * Ogni consumer passa solo:
 *   - open           bool
 *   - onClose        () => void
 *   - onSaved        (bolla) => void            (opzionale)
 *   - cantieri       array cantieri per la select
 *   - initialCantiereId  cantiere pre-selezionato (opzionale)
 *   - editingBolla   oggetto bolla in edit (opzionale)
 *
 * Tutta la logica (stato form, allegati, mutation, autosave anti-perdita
 * dati) resta dentro questo wrapper, così qualunque modifica al form
 * principale in `BollaForm.jsx` si riflette automaticamente ovunque.
 */
import React, { useState, useEffect, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api";
import BollaForm from "@/pages/bolle/BollaForm";
import { codiceBolla } from "@/lib/codici";

const emptyBolla = (cantiereId = "") => ({
  numero_bolla: "",
  data: new Date().toISOString().slice(0, 10),
  fornitore: "",
  cantiere_id: cantiereId || "",
  materiale: "",
  quantita: "",
  unita_misura: "pz",
  prezzo_unitario: "",
  aliquota_iva: "22",
  categoria: "Materiali",
  numero_ordine: "",
  note: "",
  allegati: [],
  fornitore_partita_iva: "",
  fornitore_indirizzo: "",
});

const fromBolla = (b) => ({
  numero_bolla: b.numero_bolla || "",
  data: b.data || new Date().toISOString().slice(0, 10),
  fornitore: b.fornitore || "",
  cantiere_id: b.cantiere_id || "",
  materiale: b.materiale || "",
  quantita: b.quantita != null ? String(b.quantita) : "",
  unita_misura: b.unita_misura || "pz",
  prezzo_unitario: b.prezzo_unitario != null ? String(b.prezzo_unitario) : "",
  aliquota_iva: b.aliquota_iva != null ? String(b.aliquota_iva) : "22",
  categoria: b.categoria || "Materiali",
  numero_ordine: b.numero_ordine || "",
  note: b.note || "",
  allegati: b.allegati || [],
  fornitore_partita_iva: b.fornitore_partita_iva || "",
  fornitore_indirizzo: b.fornitore_indirizzo || "",
});

const numIt = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return v;
  const n = parseFloat(String(v).trim().replace(/\s/g, "").replace(",", "."));
  return isNaN(n) ? 0 : n;
};

export default function BollaFormModal({
  open,
  onClose,
  onSaved,
  cantieri = [],
  initialCantiereId = "",
  editingBolla = null,
}) {
  const qc = useQueryClient();
  const editingId = editingBolla?.bolla_id || null;

  const [form, setForm] = useState(() =>
    editingBolla ? fromBolla(editingBolla) : emptyBolla(initialCantiereId)
  );

  // Reset del form ad ogni apertura per evitare che dati vecchi restino visibili
  useEffect(() => {
    if (!open) return;
    setForm(editingBolla ? fromBolla(editingBolla) : emptyBolla(initialCantiereId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingId, initialCantiereId]);

  const handleFiles = async (files) => {
    for (const file of Array.from(files || [])) {
      if (file.size > 6 * 1024 * 1024) {
        toast.error(`"${file.name}" troppo grande (max 6 MB)`);
        continue;
      }
      // eslint-disable-next-line no-await-in-loop
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      }).catch(() => null);
      if (b64) setForm((f) => ({ ...f, allegati: [...f.allegati, b64] }));
    }
  };

  const removeAllegato = (idx) =>
    setForm((f) => ({ ...f, allegati: f.allegati.filter((_, i) => i !== idx) }));

  // Best-effort: aggiorna/crea anagrafica fornitore quando salvi una bolla
  const upsertFornitore = async (payload) => {
    if (!payload.fornitore) return;
    if (!payload.fornitore_partita_iva && !payload.fornitore_indirizzo) return;
    try {
      await api.post("/entita/fornitori", {
        ragione_sociale: payload.fornitore,
        partita_iva: payload.fornitore_partita_iva || "",
        indirizzo: payload.fornitore_indirizzo || "",
        categoria: payload.categoria || "Materiali",
      });
    } catch (_) {
      // fornitore già presente o servizio non disponibile → best-effort
    }
  };

  const save = useMutation({
    mutationFn: async (payload) => {
      upsertFornitore(payload);
      const { fornitore_partita_iva, fornitore_indirizzo, ...rest } = payload;
      const clean = {
        ...rest,
        quantita: numIt(rest.quantita),
        prezzo_unitario: numIt(rest.prezzo_unitario),
        aliquota_iva: numIt(rest.aliquota_iva),
      };
      if (editingId) {
        return api.patch(`/bolle/${editingId}`, clean).then((r) => r.data);
      }
      return api.post("/bolle", clean).then((r) => r.data);
    },
    onSuccess: (data) => {
      toast.success(editingId ? "Bolla aggiornata" : "Bolla registrata");
      qc.invalidateQueries({ queryKey: ["bolle-all"] });
      qc.invalidateQueries({ queryKey: ["bolle"] });
      qc.invalidateQueries({ queryKey: ["cantiere-econ"] });
      qc.invalidateQueries({ queryKey: ["fornitori"] });
      qc.invalidateQueries({ queryKey: ["fornitori-stats"] });
      if (typeof onSaved === "function") onSaved(data);
      onClose();
    },
    onError: (e) => {
      toast.error(e.response?.data?.detail || "Errore salvataggio bolla");
    },
  });

  const anteprima = useMemo(() => {
    const q = numIt(form.quantita);
    const p = numIt(form.prezzo_unitario);
    const iva = numIt(form.aliquota_iva);
    const imp = q * p;
    return {
      imponibile: imp.toFixed(2),
      iva: (imp * iva / 100).toFixed(2),
      totale: (imp * (1 + iva / 100)).toFixed(2),
    };
  }, [form.quantita, form.prezzo_unitario, form.aliquota_iva]);

  if (!open) return null;

  return (
    <BollaForm
      editingId={editingId}
      form={form}
      setForm={setForm}
      cantieri={cantieri}
      anteprima={anteprima}
      codiceBolla={codiceBolla}
      onClose={onClose}
      onSubmit={(f) => save.mutateAsync(f)}
      saving={save.isPending}
      handleFiles={handleFiles}
      removeAllegato={removeAllegato}
    />
  );
}
